import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { T3AnalysisRun, T3AnalysisStore } from '../../../core/shared/t3-model/t3-analysis.store';
import { T3ApiResponse, T3Year } from '../../../core/shared/t3-model/t3-response-model';

type HorizonYears = 5 | 10 | 15 | 20;

type ViewCard = {
  id: 'total_ht' | 'undiagnosed' | 'diagnosed' | 'treated' | 'controlled';
  title: string;
  value: number;
  percent: number;
  barPercent: number;
  tooltip?: string | null;
};

@Component({
  selector: 'app-t3-stats',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  templateUrl: './t3-stats.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class T3StatsComponent implements OnInit, OnDestroy, OnChanges {
  private store = inject(T3AnalysisStore);
  private cdr = inject(ChangeDetectorRef);
  private destroyed$ = new Subject<void>();

  @Input() tooltipTotalOverride: string | null = null;
  @Input() enableTooltip = true;
  @Input() horizon: HorizonYears = 5;

  selectedSimulationTitle = '-';
  cards: ViewCard[] = [];

  private currentRun?: T3AnalysisRun;

  ngOnInit(): void {
    combineLatest([this.store.runsList$, this.store.selectedRunId$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([runs, selectedRunId]) => {
        const selectedRun = selectedRunId
          ? runs.find((r) => r.id === selectedRunId)
          : runs.length
            ? runs[runs.length - 1]
            : undefined;

        this.currentRun = selectedRun;
        this.selectedSimulationTitle = selectedRun?.title ?? '-';
        this.cards = this.buildCards(selectedRun);
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['horizon'] && this.currentRun) {
      this.cards = this.buildCards(this.currentRun);
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get tooltipTotal(): string | null {
    if (!this.enableTooltip) return null;

    if (this.tooltipTotalOverride?.trim()) {
      return this.tooltipTotalOverride.trim();
    }

    return this.cards.length ? this.cards[0].tooltip ?? null : null;
  }

  getCard(index: number): ViewCard | null {
    return this.cards[index] ?? null;
  }

  private buildCards(run?: T3AnalysisRun): ViewCard[] {
    if (!run?.t3) return [];

    const t3 = run.t3;
    const year = this.pickHorizonYearOrLatest(t3, this.horizon);

    const undiagnosed = this.round0(this.store.pick(t3, 'Undiagnosed', year));
    const diagnosed = this.round0(this.store.pick(t3, 'Diagnosed', year));
    const treated = this.round0(this.store.pick(t3, 'Treated', year));
    const controlled = this.round0(this.store.pick(t3, 'Controlled', year));

    const totalHt = this.round0(undiagnosed + diagnosed);

    const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

    const undiagnosedPct = pct(undiagnosed, totalHt);
    const diagnosedPct = pct(diagnosed, totalHt);
    const treatedPct = pct(treated, diagnosed);
    const controlledPct = pct(controlled, diagnosed);

    const totalTooltip = totalHt > 0
      ? `Total HT population: ${this.formatNumber(totalHt)}.`
      : null;

    return [
      this.card('total_ht', 'Total HT Population', totalHt, 100, totalTooltip),
      this.card('undiagnosed', 'Undiagnosed', undiagnosed, undiagnosedPct),
      this.card('diagnosed', 'Diagnosed', diagnosed, diagnosedPct),
      this.card('treated', 'Treated', treated, treatedPct),
      this.card('controlled', 'Controlled', controlled, controlledPct)
    ];
  }

  private pickHorizonYearOrLatest(t3: T3ApiResponse, horizon: HorizonYears): T3Year | null {
    const targetYear =
      horizon === 5 ? 2031 :
      horizon === 10 ? 2036 :
      horizon === 15 ? 2041 :
      2046;

    const years = Array.isArray(t3?.years) ? t3.years.map(Number).filter(Number.isFinite) : [];

    if (!years.length) return null;
    if (years.includes(targetYear)) return targetYear;

    return years[years.length - 1];
  }

  private card(
    id: ViewCard['id'],
    title: string,
    value: number,
    percent: number,
    tooltip?: string | null
  ): ViewCard {
    const barPercent = Math.max(0, Math.min(100, percent));

    return {
      id,
      title,
      value,
      percent,
      barPercent,
      tooltip: tooltip ?? null
    };
  }

  private round0(value: any): number {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : 0;
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US').format(Math.round(Number(value)));
  }
}